/**
 * Pure parser for IFS document filenames (browse check-in auto-population).
 *
 * Supported formats:
 *   <Title> (<DocClass> - <DocNo> - <DocSheet> - <DocRev>) - <Version>.<ext>
 *   <Title> [<DocClass> - <DocNo> - <DocSheet> - <DocRev>] - <Version>.<ext>
 *   <DocClass>-<DocNo>-<DocSheet>-<DocRev>.<ext>
 */
const COPY_PREFIX_PATTERN = /^copy of\s+/i;
const VERSION_SUFFIX_PATTERN = /\s*-\s*\d+\s*$/;
const BRACKET_PATTERN = /\(([^)]+)\)|\[([^\]]+)\]/g;
const REMAINING_BRACKET_PATTERN = /\(.+?\)|\[.+?\]/g;
const ALT_PATTERN = /([A-Z0-9-]+)\s*[-_]\s*(\d+)\s*[-_]\s*(\d+)\s*[-_]\s*([A-Z0-9]+)/i;

function isIFSMetadataPattern(str) {
  const tokens = str
    .split(/[-_\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length < 4) {
    return false;
  }
  const docNo = tokens[tokens.length - 3];
  const docSheet = tokens[tokens.length - 2];
  const docRev = tokens[tokens.length - 1];
  return /^\d+$/.test(docNo) && /^\d+$/.test(docSheet) && /^[A-Z0-9]+$/i.test(docRev);
}

function buildResult({ title, docClass, docNo, docSheet, docRev, fileExtension, isIFSFormat, isCopy }) {
  return {
    title,
    docClass,
    docNo,
    docSheet,
    docRev,
    fileExtension,
    isIFSFormat,
    isCopy,
  };
}

export function parseIFSFileName(filename) {
  if (!filename || typeof filename !== "string") {
    return null;
  }

  const isCopy = COPY_PREFIX_PATTERN.test(filename);
  const cleanFileName = filename.replace(COPY_PREFIX_PATTERN, "").trim();

  const dotIndex = cleanFileName.lastIndexOf(".");
  const fileExtension =
    dotIndex >= 0 ? cleanFileName.slice(dotIndex + 1).toUpperCase() : "";
  const nameWithoutExt = dotIndex >= 0 ? cleanFileName.slice(0, dotIndex) : cleanFileName;

  const withoutVersion = nameWithoutExt.replace(VERSION_SUFFIX_PATTERN, "");

  const bracketMatches = [...withoutVersion.matchAll(BRACKET_PATTERN)].sort(
    (a, b) => a.index - b.index
  );

  let metadataString = null;
  let metadataIndex = -1;
  let titlePart = withoutVersion.trim();

  if (bracketMatches.length > 0) {
    for (let i = bracketMatches.length - 1; i >= 0; i--) {
      const match = bracketMatches[i];
      const candidate = match[1] || match[2];
      if (isIFSMetadataPattern(candidate)) {
        metadataString = candidate;
        metadataIndex = match.index;
        break;
      }
    }
    if (metadataIndex >= 0) {
      titlePart = withoutVersion.slice(0, metadataIndex).replace(/[\s()[\]-]+$/, "").trim();
      titlePart = titlePart.replace(REMAINING_BRACKET_PATTERN, "").trim();
    }
  }

  if (!metadataString) {
    const altMatch = withoutVersion.match(ALT_PATTERN);
    if (altMatch) {
      return buildResult({
        title: withoutVersion.slice(0, altMatch.index).replace(/[\s()[\]-]+$/, "").trim() || nameWithoutExt,
        docClass: altMatch[1].trim(),
        docNo: altMatch[2].trim(),
        docSheet: altMatch[3].trim(),
        docRev: altMatch[4].trim().toUpperCase(),
        fileExtension,
        isIFSFormat: true,
        isCopy,
      });
    }
    return buildResult({
      title: nameWithoutExt.trim(),
      docClass: "",
      docNo: "",
      docSheet: "",
      docRev: "",
      fileExtension,
      isIFSFormat: false,
      isCopy,
    });
  }

  const tokens = metadataString
    .split(/\s+-\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length < 4) {
    return buildResult({
      title: titlePart || nameWithoutExt,
      docClass: "",
      docNo: "",
      docSheet: "",
      docRev: "",
      fileExtension,
      isIFSFormat: false,
      isCopy,
    });
  }

  const docRev = tokens.pop();
  const docSheet = tokens.pop();
  const docNo = tokens.pop();
  const docClass = tokens.join(" - ").trim();

  return buildResult({
    title: titlePart || nameWithoutExt,
    docClass,
    docNo,
    docSheet,
    docRev: docRev.toUpperCase(),
    fileExtension,
    isIFSFormat: true,
    isCopy,
  });
}
